/**
 * ショット軌跡のストリーミング再生（自前・GPL非依存）。
 *
 * 端末間（iPhone↔PC 等）では物理エンジンの浮動小数演算が一致せず、ローカル再生では
 * 球の動きが食い違う（特にブレイク）。そこで「撞いた側（権威）が自分の物理計算中に
 * 球位置を一定間隔でサンプリングしてライブ送信し、相手はそれを補間再生する」方式にする。
 *   - 送信側: createTrackRecorder（~30Hz・全 on-table 球を量子化して蓄積→バッチ送出）
 *   - 受信側: createTrackPlayer（届いたサンプルを貯め、指定時刻の位置を球ごとに線形補間）
 * 回転は受信側で「移動量から転がり回転を導出」するため送らない（world.applyPlaybackFrame）。
 * 最終静止配置・ルールは従来どおり shotEnd（権威スナップショット）で確定する。
 */

/** 1 サンプル: ms（ショット開始からの経過）＋ p=[id, x*Q, y*Q, ...]（量子化整数）。 */
export interface TrackSample {
  readonly ms: number;
  readonly p: number[];
}

const Q = 1000; // 量子化係数（m → mm 整数）
/** サンプリング間隔（約30Hz）。これ未満の間隔のサンプルは間引く。 */
export const TRACK_SAMPLE_MS = 33;
/** 受信側の再生バッファ（ネットワーク揺らぎ吸収・この分だけ遅延再生）。 */
export const TRACK_BUFFER_MS = 200;

export interface BallPos {
  readonly id: number;
  readonly x: number;
  readonly y: number;
}

export interface TrackRecorder {
  /** ms 時点の球位置を記録（間引き済み）。 */
  sample(ms: number, balls: ReadonlyArray<BallPos>): void;
  /** 未送信サンプルを取り出してクリア（バッチ送信用）。 */
  drain(): TrackSample[];
}

/** 送信側の軌跡レコーダ。 */
export const createTrackRecorder = (): TrackRecorder => {
  let pending: TrackSample[] = [];
  let lastMs = -Infinity;
  return {
    sample(ms: number, balls: ReadonlyArray<BallPos>): void {
      if (ms - lastMs < TRACK_SAMPLE_MS) return;
      lastMs = ms;
      const p: number[] = [];
      for (const b of balls) {
        p.push(b.id, Math.round(b.x * Q), Math.round(b.y * Q));
      }
      pending.push({ ms: Math.round(ms), p });
    },
    drain(): TrackSample[] {
      const out = pending;
      pending = [];
      return out;
    },
  };
};

interface Key {
  ms: number;
  x: number;
  y: number;
}

export interface TrackPlayer {
  /** 受信サンプルを取り込む。 */
  push(samples: ReadonlyArray<TrackSample>): void;
  /** 最後のサンプルの ms（再生終了判定用）。 */
  lastMs(): number;
  /** 1 件でもデータがあるか。 */
  hasData(): boolean;
  /**
   * ms 時点の各球位置（線形補間）。範囲外は端点でクランプ。
   * ただし「自分のトラックが全体の最終 ms より早く終わった球＝ショット中にポケットインした球」は、
   * その終了時刻を過ぎたら出力しない（入口でクランプ停止させず、呼び出し側が落球処理する）。
   */
  frameAt(ms: number): Map<number, { x: number; y: number }>;
  /**
   * ms 時点までに「早期終了した（＝ポケットインした）」球 id 一覧。
   * 全球は同一サンプルで毎フレーム記録されるため、最終キーフレームが全体の最終 ms より前で
   * 終わっている球は、ショット中に盤上集合から外れた＝落ちた球と判定できる。
   */
  pottedIds(ms: number): number[];
}

/** 受信側の軌跡プレイヤー（球ごとにキーフレーム列を保持して補間）。 */
export const createTrackPlayer = (): TrackPlayer => {
  const tracks = new Map<number, Key[]>();
  let last = 0;
  return {
    push(samples: ReadonlyArray<TrackSample>): void {
      for (const s of samples) {
        if (s.ms > last) last = s.ms;
        const p = s.p;
        for (let i = 0; i + 2 < p.length; i += 3) {
          const id = p[i]!;
          const x = p[i + 1]! / Q;
          const y = p[i + 2]! / Q;
          let arr = tracks.get(id);
          if (!arr) {
            arr = [];
            tracks.set(id, arr);
          }
          arr.push({ ms: s.ms, x, y });
        }
      }
    },
    lastMs: (): number => last,
    hasData: (): boolean => tracks.size > 0,
    frameAt: (ms: number): Map<number, { x: number; y: number }> => {
      const out = new Map<number, { x: number; y: number }>();
      tracks.forEach((arr, id) => {
        if (arr.length === 0) return;
        const first = arr[0]!;
        if (ms <= first.ms) {
          out.set(id, { x: first.x, y: first.y });
          return;
        }
        const lastKey = arr[arr.length - 1]!;
        if (ms >= lastKey.ms) {
          // 早期終了（ポケットイン）した球は、終了時刻を過ぎたら出力しない＝入口で
          // クランプ停止させない（呼び出し側が落球させる）。最後まで残った球はクランプ。
          if (lastKey.ms < last) return;
          out.set(id, { x: lastKey.x, y: lastKey.y });
          return;
        }
        for (let i = 1; i < arr.length; i++) {
          const b = arr[i]!;
          if (b.ms >= ms) {
            const a = arr[i - 1]!;
            const span = Math.max(1, b.ms - a.ms);
            const t = (ms - a.ms) / span;
            out.set(id, { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
            return;
          }
        }
      });
      return out;
    },
    pottedIds: (ms: number): number[] => {
      const ids: number[] = [];
      tracks.forEach((arr, id) => {
        if (arr.length === 0) return;
        const lastKey = arr[arr.length - 1]!;
        // 全体の最終 ms より前で記録が途切れ、その時刻を過ぎている＝ショット中に落球した球。
        if (lastKey.ms < last && ms >= lastKey.ms) ids.push(id);
      });
      return ids;
    },
  };
};
